import { Box, Typography } from '@mui/material';
import useRfidGroup from 'hooks/useRfidGroup';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import type { FC } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import pageUrls from 'constants/pageUrls';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';
import Modal from 'components/Modal';
import GenericSettingComponent from 'components/SettingComponents/GenericSettingComponent';
import StatusMessageBox from 'components/StatusMessageBox';

import { useAppDispatch } from 'redux-store';
import type { RfidGroupId } from 'redux-store/slices/rfidGroups/types';
import deleteRfidGroupThunk from 'redux-store/thunks/rfidGroups/deleteRfidGroupThunk';
import getAllRfidGroupsThunk from 'redux-store/thunks/rfidGroups/getAllRfidGroupsThunk';

export interface RemoveRfidGroupModalProps extends DefaultPopupProps {
  groupId: RfidGroupId | null;
}

type ErrorMessage = string;
type Successful = true;
type Loading = false;
type Nothing = null;

type GroupState = ErrorMessage | Successful | Loading | Nothing;

const RemoveRfidGroupModal: FC<RemoveRfidGroupModalProps> = ({
  onClose,
  groupId,
  open,
}) => {
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const { t } = useTranslation();

  const [groupState, setGroupState] = useState<GroupState>(null);

  const removeRfidGroups = useCallback(async () => {
    if (groupId === null) return;

    setGroupState(null);

    const handleDeleteRfidGroup = async (): Promise<boolean> => {
      try {
        setGroupState(false);
        const result = await dispatch(
          deleteRfidGroupThunk({ groupId }),
        ).unwrap();

        if (!result.success) {
          setGroupState(t('common:pages.settings.remove_device_group_error'));
        } else {
          setGroupState(true);

          return true;
        }
      } catch (e) {
        console.error('error deleting rfid group', e);
        setGroupState(t('common:pages.settings.remove_device_group_error'));
      }

      return false;
    };

    setLoading(true);

    if (await handleDeleteRfidGroup()) {
      setSuccess(true);

      setError(null);

      await dispatch(getAllRfidGroupsThunk({})).unwrap();

      setTimeout(async () => {
        await router.push(pageUrls.rfidGroups.all);
        onClose();
        setSuccess(false);
      }, 1000);
    } else {
      setError(t('common:pages.settings.remove_device_group_error'));
    }

    setLoading(false);
  }, [dispatch, groupId, t, onClose, router]);

  const rfidGroup = useRfidGroup(groupId ?? undefined);

  if (!rfidGroup) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose} hideCloseButton={success}>
      {!success ? (
        <Box
          height="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ overflowY: 'auto' }}
        >
          <Box mb={4}>
            <Typography
              variant="h2"
              textAlign="center"
              mb={2}
              data-testid="remove-rfid-group-modal"
            >
              {t('common:pages.settings.remove_rfid_group')}
            </Typography>
            <Typography variant="body1" textAlign="center">
              {t('common:pages.settings.remove_device_group_description')}
            </Typography>
            <Typography variant="body1" textAlign="center">
              {t('common:pages.settings.remove_device_group_description_2')}
            </Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            overflow="auto"
            flexGrow={0}
            flexShrink={1}
            flexBasis="auto"
            width="100%"
            mb={4}
          >
            <GenericSettingComponent
              fullWidth
              color={
                groupState === true
                  ? 'success'
                  : typeof groupState === 'string'
                  ? 'error'
                  : undefined
              }
              leftText={
                typeof groupState === 'string'
                  ? (groupState as string)
                  : rfidGroup.name
              }
              passableProps={null}
              disableHideWhenOffline
            />
          </Box>
          <Box display="flex" flexDirection="row" gap={1} width="100%">
            <Button
              onClick={onClose}
              color="neutral"
              fullWidth
              variant="flat"
              size="large"
              data-testid="cancel-button"
            >
              {t('common:cancel')}
            </Button>
            <Button
              disabled={isLoading}
              onClick={removeRfidGroups}
              fullWidth
              size="large"
              variant="flat"
              color="error"
              data-testid="submit"
            >
              {t('common:pages.rfid_groups.remove_group')}
            </Button>
          </Box>
          <ErrorText error={error} mt={2} />
        </Box>
      ) : (
        <StatusMessageBox
          type="success"
          message={t('common:pages.settings.device_group_removed')}
        />
      )}
    </Modal>
  );
};

export default RemoveRfidGroupModal;
